// appEvents.js
class AppEvents {
  constructor() {
    this.listeners = new Map();
  }

  /**
   * Подписка на событие
   * @param {string} event - Название события
   * @param {Function} callback - Функция-обработчик
   * @returns {Function} Функция для отписки
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
    
    // Возвращаем функцию для отписки
    return () => {
      this.listeners.get(event)?.delete(callback);
      if (this.listeners.get(event)?.size === 0) {
        this.listeners.delete(event);
      }
    };
  }

  /**
   * Отписка от события
   * @param {string} event - Название события
   * @param {Function} callback - Функция-обработчик
   */
  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
      if (this.listeners.get(event).size === 0) {
        this.listeners.delete(event);
      }
    }
  }

  /**
   * Отправка события
   * @param {string} event - Название события
   * @param {any} data - Данные события
   */
  emit(event, data) {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Ошибка в обработчике события ${event}:`, error);
        }
      });
    }
  }

  /**
   * Подписка на одноразовое событие
   * @param {string} event - Название события
   * @param {Function} callback - Функция-обработчик
   * @returns {Function} Функция для отписки
   */
  once(event, callback) {
    const wrapper = (data) => {
      callback(data);
      this.off(event, wrapper);
    };
    return this.on(event, wrapper);
  }

  /**
   * Удаление всех подписчиков события
   * @param {string} [event] - Название события (опционально)
   */
  removeAllListeners(event) {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
  }

  /**
   * Получение количества подписчиков для события
   * @param {string} event - Название события
   * @returns {number} Количество подписчиков
   */
  listenerCount(event) {
    return this.listeners.get(event)?.size || 0;
  }

  /**
   * Получение всех активных событий
   * @returns {string[]} Массив названий событий
   */
  getActiveEvents() {
    return Array.from(this.listeners.keys());
  }
}

// Экспортируем синглтон
export const appEvents = new AppEvents();

// Константы событий
export const APP_EVENTS = {
  // Общие события
  DATA_CHANGED: 'app:data-changed',
  CACHE_INVALIDATED: 'app:cache-invalidated',
  ALL_DATA_CHANGED: 'app:all-data-changed',

  // Обучения (Trainings)
  TRAINING_COMPLETED: 'app:training-completed',
  TRAINING_ASSIGNED: 'app:training-assigned',
  TRAINING_EXTENDED: 'app:training-extended',
  TRAINING_DELETED: 'app:training-deleted',
  TRAINING_STATUS_CHANGED: 'app:training-status-changed',

  // Записи на мероприятия (Enrollments)
  ENROLLMENT_CREATED: 'app:enrollment-created',
  ENROLLMENT_CANCELLED: 'app:enrollment-cancelled',

  // Курсы (Courses)
  COURSE_CREATED: 'app:course-created',
  COURSE_UPDATED: 'app:course-updated',
  COURSE_DELETED: 'app:course-deleted',

  // Сотрудники (Employees)
  EMPLOYEE_CREATED: 'app:employee-created',
  EMPLOYEE_UPDATED: 'app:employee-updated',
  EMPLOYEE_DELETED: 'app:employee-deleted',

  // Мероприятия (Events)
  EVENT_CREATED: 'app:event-created',
  EVENT_UPDATED: 'app:event-updated',
  EVENT_DELETED: 'app:event-deleted',

  // Матрица компетенций (Matrix)
  MATRIX_UPDATED: 'app:matrix-updated',

  // Бригады (Brigades)
  BRIGADE_CREATED: 'app:brigade-created',
  BRIGADE_UPDATED: 'app:brigade-updated',
  BRIGADE_DELETED: 'app:brigade-deleted',

  // Должности (Positions)
  POSITION_CREATED: 'app:position-created',
  POSITION_UPDATED: 'app:position-updated',
  POSITION_DELETED: 'app:position-deleted',

  // Пользователи (Users)
  USER_CREATED: 'app:user-created',
  USER_UPDATED: 'app:user-updated',
  USER_DELETED: 'app:user-deleted',
  USER_LOGGED_IN: 'app:user-logged-in',
  USER_LOGGED_OUT: 'app:user-logged-out'
};

// Группы событий для удобного массового подписывания
export const EVENT_GROUPS = {
  // Все события, связанные с обучением
  TRAINING_RELATED: [
    APP_EVENTS.TRAINING_COMPLETED,
    APP_EVENTS.TRAINING_ASSIGNED,
    APP_EVENTS.TRAINING_EXTENDED,
    APP_EVENTS.TRAINING_DELETED,
    APP_EVENTS.TRAINING_STATUS_CHANGED
  ],

  // Все события, связанные с мероприятиями
  EVENT_RELATED: [
    APP_EVENTS.EVENT_CREATED,
    APP_EVENTS.EVENT_UPDATED,
    APP_EVENTS.EVENT_DELETED,
    APP_EVENTS.ENROLLMENT_CREATED,
    APP_EVENTS.ENROLLMENT_CANCELLED
  ],

  // Все события, связанные с сотрудниками
  EMPLOYEE_RELATED: [
    APP_EVENTS.EMPLOYEE_CREATED,
    APP_EVENTS.EMPLOYEE_UPDATED,
    APP_EVENTS.EMPLOYEE_DELETED
  ],

  // Все события, связанные с курсами
  COURSE_RELATED: [
    APP_EVENTS.COURSE_CREATED,
    APP_EVENTS.COURSE_UPDATED,
    APP_EVENTS.COURSE_DELETED
  ],

  // Все события, требующие обновления дашборда
  DASHBOARD_RELATED: [
    APP_EVENTS.TRAINING_COMPLETED,
    APP_EVENTS.TRAINING_ASSIGNED,
    APP_EVENTS.ENROLLMENT_CREATED,
    APP_EVENTS.ENROLLMENT_CANCELLED,
    APP_EVENTS.EMPLOYEE_UPDATED,
    APP_EVENTS.COURSE_UPDATED,
    APP_EVENTS.MATRIX_UPDATED
  ],

  // Все события, требующие обновления кеша
  CACHE_INVALIDATION_RELATED: [
    APP_EVENTS.DATA_CHANGED,
    APP_EVENTS.ALL_DATA_CHANGED,
    APP_EVENTS.CACHE_INVALIDATED,
    APP_EVENTS.TRAINING_COMPLETED,
    APP_EVENTS.TRAINING_ASSIGNED,
    APP_EVENTS.ENROLLMENT_CREATED,
    APP_EVENTS.ENROLLMENT_CANCELLED,
    APP_EVENTS.EVENT_CREATED,
    APP_EVENTS.EVENT_UPDATED,
    APP_EVENTS.EVENT_DELETED,
    APP_EVENTS.COURSE_CREATED,
    APP_EVENTS.COURSE_UPDATED,
    APP_EVENTS.EMPLOYEE_CREATED,
    APP_EVENTS.EMPLOYEE_UPDATED,
    APP_EVENTS.MATRIX_UPDATED
  ]
};

// Хелпер для удобного логирования событий (для разработки)
export const enableEventLogging = () => {
  const originalEmit = appEvents.emit.bind(appEvents);
  appEvents.emit = (event, data) => {
    console.log(`📡 [Event] ${event}`, data);
    return originalEmit(event, data);
  };
  console.log('✅ Логирование событий включено');
};

// Хелпер для отключения логирования
export const disableEventLogging = () => {
  // Восстановление оригинального метода emit невозможно без сохранения ссылки
  // Для production используйте enableEventLogging только в development
  console.warn('⚠️ Отключение логирования событий: перезагрузите страницу для полного отключения');
};